/* eslint-disable */
import { useEffect, useState } from "react";
import { Upload, Star, X, GripVertical } from "lucide-react";
import { propertiesApi, photoUrl } from "./api";
import "./PhotosEditor.css";

/**
 * Shared photo editor used by both CreateListing and EditListing.
 * Owns its own photo list (loads from props.initial, then keeps in
 * sync as the user uploads / reorders / removes). Supports drag-to-
 * reorder with display_order persisted via PATCH per affected row.
 */
export default function PhotosEditor({ listingId, initial = [], onChange }) {
  const [photos, setPhotos] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  useEffect(() => {
    setPhotos(initial);
  }, [initial]);

  const propagate = (next) => {
    setPhotos(next);
    onChange && onChange(next);
  };

  const upload = async (file) => {
    if (!file || !listingId) return;
    setBusy(true);
    setError("");
    try {
      const p = await propertiesApi.uploadPhoto(
        listingId,
        file,
        photos.length === 0
      );
      propagate([...photos, p]);
    } catch (err) {
      setError(err.message || "Could not upload");
    } finally {
      setBusy(false);
    }
  };

  const setPrimary = async (photoId) => {
    try {
      await propertiesApi.patchPhoto(listingId, photoId, { is_primary: true });
      propagate(
        photos.map((p) => ({ ...p, is_primary: p.id === photoId }))
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (photoId) => {
    try {
      await propertiesApi.deletePhoto(listingId, photoId);
      propagate(photos.filter((p) => p.id !== photoId));
    } catch (err) {
      setError(err.message);
    }
  };

  const onDragStart = (idx) => () => setDragIndex(idx);
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (idx !== dropTarget) setDropTarget(idx);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setDropTarget(null);
  };
  const onDrop = (idx) => async (e) => {
    e.preventDefault();
    if (dragIndex == null || dragIndex === idx) {
      onDragEnd();
      return;
    }
    const next = [...photos];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    propagate(next);
    onDragEnd();
    // Persist new display_order for every affected row (cheap at <=20 photos).
    try {
      await Promise.all(
        next.map((p, i) =>
          p.display_order === i
            ? null
            : propertiesApi.patchPhoto(listingId, p.id, { display_order: i })
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <label className="vsp-pe-drop">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) upload(f);
          }}
          disabled={busy || photos.length >= 20}
        />
        <Upload size={26} strokeWidth={1.6} />
        <p>
          {photos.length >= 20
            ? "Maximum 20 photos reached"
            : "Click to upload a photo"}
        </p>
        <span>JPG / PNG / WebP up to 8 MB · {photos.length} / 20</span>
      </label>

      {photos.length > 0 && (
        <>
          <p className="vsp-pe-help">
            Drag tiles to reorder. The first photo (or starred one) is what
            shows on browse cards.
          </p>
          <div className="vsp-pe-grid">
            {photos.map((p, idx) => (
              <div
                key={p.id}
                className={`vsp-pe-photo ${dropTarget === idx ? "drop" : ""} ${
                  dragIndex === idx ? "dragging" : ""
                }`}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragEnd={onDragEnd}
                onDrop={onDrop(idx)}
              >
                <img src={photoUrl(p.url)} alt="" />
                <span className="vsp-pe-grip" aria-hidden>
                  <GripVertical size={12} strokeWidth={2.2} />
                </span>
                <button
                  type="button"
                  className={`vsp-pe-star ${p.is_primary ? "active" : ""}`}
                  onClick={() => setPrimary(p.id)}
                  title={p.is_primary ? "Primary photo" : "Set as primary"}
                >
                  <Star
                    size={12}
                    fill={p.is_primary ? "#F59E0B" : "none"}
                    color={p.is_primary ? "#F59E0B" : "#fff"}
                  />
                </button>
                <button
                  type="button"
                  className="vsp-pe-remove"
                  onClick={() => remove(p.id)}
                  title="Remove"
                >
                  <X size={12} />
                </button>
                {p.is_primary && <span className="vsp-pe-pri">Primary</span>}
              </div>
            ))}
          </div>
        </>
      )}

      {error && <div className="vsp-pe-error">{error}</div>}
    </>
  );
}
